import type { ReactElement } from 'react';
import type { Route } from './+types/_app.folder.$folderId';
import type { ShopperExperience } from '@/scapi';
import { Link } from '@/components/link';
import { SeoMeta } from '@/components/seo-meta';
import { buildCanonicalUrl } from '@/utils/canonical-url';
import { getLogger } from '@/lib/logger.server';
import { fetchContentFolderBrowserData, type ContentFolderListItem } from '@/lib/api/content-library.server';
import { throwLoaderResponseFromError, throwNotFoundResponse } from '@/lib/api/loader-response.server';
import { routeHref, routes } from '@/route-paths';
import { useTranslation } from 'react-i18next';

export type ContentFolderPageData = {
    folder: ShopperExperience.schemas['ContentFolder'];
    contentAssets: ContentFolderListItem[];
    folderId: string;
    pageUrl: string;
};

/** Prefer folder SEO fields, then display name, then the URL folder ID. */
export function resolveContentFolderSeoTitle(
    folder: ShopperExperience.schemas['ContentFolder'],
    folderId: string
): string {
    return folder.pageTitle?.trim() || folder.name?.trim() || folderId;
}

export function resolveContentFolderSeoDescription(
    folder: ShopperExperience.schemas['ContentFolder']
): string | undefined {
    const description = folder.pageDescription?.trim() || folder.description?.trim();
    return description || undefined;
}

export async function loader(args: Route.LoaderArgs): Promise<ContentFolderPageData> {
    const { folderId } = args.params;
    const logger = getLogger(args.context);
    if (!folderId) {
        throwNotFoundResponse();
    }

    const requestUrl = new URL(args.request.url);

    try {
        const { folder, contentAssets } = await fetchContentFolderBrowserData(args.context, folderId, 1);

        return {
            folder,
            contentAssets,
            folderId,
            pageUrl: buildCanonicalUrl(requestUrl.origin, requestUrl.pathname, requestUrl.search),
        };
    } catch (error) {
        throwLoaderResponseFromError(error, {
            logger,
            logMessage: 'ContentFolderPage: loader failed',
            logContext: { folderId },
        });
    }
}

function ContentFolderListSection({
    title,
    children,
    testId,
}: {
    title: string;
    children: ReactElement;
    testId: string;
}): ReactElement {
    return (
        <section className="space-y-4" data-testid={testId}>
            <h2 className="text-xl font-semibold tracking-tight text-foreground">{title}</h2>
            {children}
        </section>
    );
}

/**
 * Content library folder browser at /:siteId/:localeId/folder/:folderId.
 * Lists immediate subfolders and content assets directly assigned to this folder.
 * Assets in nested subfolders appear on those subfolder pages, not here.
 */
export default function ContentFolderPage({ loaderData }: { loaderData: ContentFolderPageData }): ReactElement {
    const { folder, contentAssets, folderId, pageUrl } = loaderData;
    const { t } = useTranslation('contentFolder');
    const subfolders = folder.folders ?? [];
    const seoTitle = resolveContentFolderSeoTitle(folder, folderId);
    const seoDescription = resolveContentFolderSeoDescription(folder);
    const displayTitle = folder.name?.trim() || folderId;
    const displayDescription = folder.description?.trim();

    return (
        <div className="section-container w-full py-8 md:py-12" data-testid="content-folder-page">
            <SeoMeta
                title={seoTitle}
                description={seoDescription}
                noIndex
                noFollow
                openGraph={{ type: 'website', url: pageUrl }}
            />

            {folder.parentFolderId && folder.parentFolderId !== 'root' ? (
                <nav className="mb-6" aria-label={t('parentFolder')}>
                    <Link
                        to={routeHref(routes.contentFolder, { folderId: folder.parentFolderId })}
                        className="text-sm text-muted-foreground hover:text-foreground">
                        {t('backToParent')}
                    </Link>
                </nav>
            ) : null}

            <header className="mb-10 space-y-3">
                <h1 className="text-3xl font-bold tracking-tight text-foreground md:text-4xl">{displayTitle}</h1>
                {displayDescription ? <p className="max-w-3xl text-muted-foreground">{displayDescription}</p> : null}
            </header>

            <div className="space-y-10">
                {subfolders.length > 0 ? (
                    <ContentFolderListSection title={t('subfolders')} testId="content-folder-subfolders">
                        <ul className="divide-y divide-border rounded-lg border border-border">
                            {subfolders.map((subfolder) => (
                                <li key={subfolder.id}>
                                    <Link
                                        to={routeHref(routes.contentFolder, { folderId: subfolder.id })}
                                        className="flex flex-col gap-1 px-4 py-4 transition-colors hover:bg-muted/50">
                                        <span className="font-medium text-foreground">
                                            {subfolder.name?.trim() || subfolder.id}
                                        </span>
                                        {subfolder.description?.trim() ? (
                                            <span className="text-sm text-muted-foreground">
                                                {subfolder.description}
                                            </span>
                                        ) : null}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </ContentFolderListSection>
                ) : null}

                {contentAssets.length > 0 ? (
                    <ContentFolderListSection title={t('contentAssets')} testId="content-folder-assets">
                        <ul className="divide-y divide-border rounded-lg border border-border">
                            {contentAssets.map((asset) => (
                                <li key={asset.id}>
                                    <Link
                                        to={routeHref(routes.contentAsset, { contentAssetId: asset.id })}
                                        className="flex flex-col gap-1 px-4 py-4 transition-colors hover:bg-muted/50">
                                        <span className="font-medium text-foreground">
                                            {asset.name?.trim() || asset.id}
                                        </span>
                                        {asset.description?.trim() ? (
                                            <span className="text-sm text-muted-foreground">{asset.description}</span>
                                        ) : null}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </ContentFolderListSection>
                ) : null}

                {subfolders.length === 0 && contentAssets.length === 0 ? (
                    <p className="text-muted-foreground" data-testid="content-folder-empty">
                        {t('empty')}
                    </p>
                ) : null}
            </div>
        </div>
    );
}
